package com.eladfinish.bible.contest

import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.window.ComposeViewport
import com.eladfinish.bible.contest.analytics.disableAnalyticsOnDevelopmentHosts
import com.eladfinish.bible.contest.appcontent.FirebaseWebApp
import com.eladfinish.bible.contest.data.common.stringResolver.WebStrings
import com.eladfinish.bible.contest.di.config.AppInfo
import com.eladfinish.bible.contest.di.config.DebugInfo
import com.eladfinish.bible.contest.di.config.KoinConfiguration
import com.eladfinish.bible.contest.di.config.initKoin
import com.eladfinish.bible.contest.logging.NapierHelper
import com.eladfinish.bible.contest.logging.suppressBenignConsoleErrors
import com.eladfinish.bible.contest.utils.applySavedLanguage
import com.eladfinish.bible.contest.utils.getLayoutDirection
import com.russhwolf.settings.Settings
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.FirebaseOptions
import dev.gitlive.firebase.initialize
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.koin.mp.KoinPlatform

private fun initializeFirebaseApp() {
    Firebase.initialize(
        context = null,
        options = FirebaseOptions(
            applicationId = FirebaseWebApp.APPLICATION_ID,
            apiKey = FirebaseWebApp.API_KEY,
            projectId = FirebaseWebApp.PROJECT_ID,
            storageBucket = FirebaseWebApp.STORAGE_BUCKET,
            authDomain = FirebaseWebApp.AUTH_DOMAIN,
            gcmSenderId = FirebaseWebApp.MESSAGING_SENDER_ID,
        ),
    )
}

// webpack replaces __BIBLE_CONTEST_DEBUG__ at build time (shared/webpack.config.d/debug-flag.js):
// true for the development server, false for the production bundle that gets deployed. The typeof
// guard keeps a bundle built without that define in release mode instead of throwing.
private fun isDebugBuild(): Boolean =
    js("typeof __BIBLE_CONTEST_DEBUG__ !== 'undefined' && __BIBLE_CONTEST_DEBUG__ === true").unsafeCast<Boolean>()

@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    val isDebug = isDebugBuild()

    suppressBenignConsoleErrors()
    NapierHelper.setupNapierLogger(debugLog = isDebug, crashlyticsLog = false)
    disableAnalyticsOnDevelopmentHosts()
    initializeFirebaseApp()

    initKoin(
        koinConfig = KoinConfiguration(
            appInfo = AppInfo(information = window.navigator.userAgent),
            debugInfo = DebugInfo(isDebugMode = isDebug),
        ),
    )

    // The language chosen in Settings, which also decides the layout direction below.
    applySavedLanguage(settings = KoinPlatform.getKoin().get<Settings>())

    MainScope().launch {
        // Fetch the translations before the first frame, so every string resolves synchronously
        // from then on (see WebStrings) instead of rendering blank until its own fetch returns.
        WebStrings.preload()

        // Mount into a constrained/centered container (see index.html), not document.body
        // directly — that's what keeps the app rendering at a mobile-sized width on desktop
        // browsers.
        ComposeViewport(document.getElementById("compose-container")!!) {
            CompositionLocalProvider(LocalLayoutDirection provides getLayoutDirection()) {
                RootContentJs()
            }
        }
    }
}
